import type { ModelConfig, ModelManifestEntry } from './ModelConfig';
import { ConfigError } from '../types';
import type { StorageProvider } from '../storage/StorageProvider';
import { RomCache } from '../storage/RomCache';
import type { UserModelStore } from './UserModelStore';

export class ModelLoader {
  private romCache?: RomCache;
  private userModelStore?: UserModelStore;

  constructor(options?: { storage?: StorageProvider; userModelStore?: UserModelStore }) {
    if (options?.storage) {
      this.romCache = new RomCache(options.storage);
    }
    this.userModelStore = options?.userModelStore;
  }

  async getBuiltinModels(): Promise<ModelManifestEntry[]> {
    const response = await fetch('/models/manifest.json');
    if (!response.ok) {
      throw new ConfigError(`Failed to load manifest: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return data as ModelManifestEntry[];
  }

  async loadBuiltinModel(modelId: string): Promise<ModelConfig> {
    const configResponse = await fetch(`/models/${modelId}/model.json`);
    if (!configResponse.ok) {
      throw new ConfigError(`Failed to load model config for ${modelId}: ${configResponse.status}`);
    }

    const modelConfig = (await configResponse.json()) as ModelConfig;

    // Use manifest romPath if available — CasioEmuNeo: GetModelFilePath(GetModelInfo("rom_path"))
    let romUrl = `/models/${modelId}/rom.bin`;
    try {
      const manifest = await this.getBuiltinModels();
      const entry = manifest.find(m => m.id === modelId);
      if (entry?.romPath) {
        romUrl = `/models/${entry.romPath}`;
      } else if (entry && !entry.romPath) {
        // Model without ROM data (e.g., fx-991cn-cw, fx-991es-plus)
        throw new ConfigError(`Model "${modelId}" is not yet available (no ROM data)`);
      }
    } catch (err) {
      if (err instanceof ConfigError) throw err;
      // Fall through with default romUrl if manifest lookup fails
    }

    if (this.romCache) {
      modelConfig.romData = await this.romCache.getOrFetch(modelId, async () => {
        const romResponse = await fetch(romUrl);
        if (!romResponse.ok) {
          throw new ConfigError(`Failed to load ROM for ${modelId}: ${romResponse.status}`);
        }
        const romArrayBuffer = await romResponse.arrayBuffer();
        return new Uint8Array(romArrayBuffer);
      });
    } else {
      const romResponse = await fetch(romUrl);
      if (!romResponse.ok) {
        throw new ConfigError(`Failed to load ROM for ${modelId}: ${romResponse.status}`);
      }
      const romArrayBuffer = await romResponse.arrayBuffer();
      modelConfig.romData = new Uint8Array(romArrayBuffer);
    }

    // Load interface.png if available — CasioEmuNeo: IMG_Load(interface_image_path) in Emulator.cpp:93-97
    const interfaceUrl = `/models/${modelId}/interface.png`;
    try {
      const interfaceResponse = await fetch(interfaceUrl);
      if (interfaceResponse.ok) {
        const blob = await interfaceResponse.blob();
        const bitmap = await createImageBitmap(blob);
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(bitmap, 0, 0);
          modelConfig.interfaceImage = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
        }
        bitmap.close();
      }
    } catch (e) {
      // interface.png is optional — proceed without it (e.g., fx-991cn-cw has none)
      console.warn(`Failed to load interface image for ${modelId}:`, e);
    }

    return modelConfig;
  }

  async loadUserModel(files: File[]): Promise<ModelConfig> {
    let modelJsonFile: File | null = null;
    let romFile: File | null = null;
    let interfaceImageFile: File | null = null;

    for (const file of files) {
      const name = file.name.toLowerCase();
      if (name === 'model.json') {
        modelJsonFile = file;
      } else if (name === 'rom.bin') {
        romFile = file;
      } else if (name === 'interface.png') {
        interfaceImageFile = file;
      }
    }

    if (!modelJsonFile) {
      throw new ConfigError('Missing required file: model.json');
    }

    if (!romFile) {
      throw new ConfigError('Missing required file: rom.bin');
    }

    const jsonText = await modelJsonFile.text();
    const modelConfig = JSON.parse(jsonText) as ModelConfig;

    const romBuffer = await romFile.arrayBuffer();
    modelConfig.romData = new Uint8Array(romBuffer);

    if (interfaceImageFile) {
      const bitmap = await createImageBitmap(interfaceImageFile);
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(bitmap, 0, 0);
        modelConfig.interfaceImage = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
      }
      bitmap.close();
    }

    return modelConfig;
  }

  async loadStoredUserModel(modelId: string): Promise<ModelConfig> {
    if (!this.userModelStore) {
      throw new ConfigError('not implemented');
    }
    const config = await this.userModelStore.loadStoredUserModel(modelId);
    if (!config) {
      throw new ConfigError(`User model not found: ${modelId}`);
    }
    return config;
  }
}
